import React, { useState, useCallback, useEffect, memo, useMemo } from 'react';
import { localizedPath } from '@/lib/localizedPath';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '@/utils/formatters';
import { useAuth } from '@/_core/hooks/useAuth';
import { getLoginUrl } from '@/const';
import { useLocation } from 'wouter';
import i18n, { SUPPORTED_LANGUAGES, getPathWithLang, getLangFromPath } from '@/i18n';

/**
 * SiteIdentityBar — Sticky top bar, globally mounted in App.tsx.
 *
 * Behaviour:
 *  - All pages except /: always visible, sticky at top, casts shadow on scroll
 *  - Home page (/): hidden while the game canvas is in view (scrollY < ~100vh).
 *    Slides in from the top (transform: translateY(-100%) → translateY(0))
 *    the moment the user scrolls past the canvas into Engine Diagnostics.
 *
 * The banner height (~40px) is exposed as a CSS custom property
 * --banner-height on <html> so scroll-margin-top can reference it globally.
 */

const BANNER_HEIGHT = 40; // px — must match actual rendered height

/** Daily-seeded random in [min, max] — stable all UTC day, changes at midnight. */
function dailyRandom(min: number, max: number): number {
  const seed = new Date().toISOString().slice(0, 10);
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(31, h) + seed.charCodeAt(i) | 0;
  }
  const norm = (h >>> 0) / 0xFFFFFFFF;
  return Math.round(min + norm * (max - min));
}

/** Shared white-pill button style */
const pillStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.95)',
  color: '#1a73e8',
  padding: '4px 12px',
  borderRadius: '4px',
  textDecoration: 'none',
  fontWeight: 600,
  fontSize: '12px',
  whiteSpace: 'nowrap',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  lineHeight: 1.4,
};

interface ChampionshipBannerProps {
  ratingSlot?: React.ReactNode;
}

// ─── Language Picker ──────────────────────────────────────────────────────────
function LanguagePicker({ currentPath }: { currentPath: string }) {
  const [open, setOpen] = React.useState(false);
  const currentLang = getLangFromPath(currentPath);
  const currentLangData = SUPPORTED_LANGUAGES.find(l => l.code === currentLang) || SUPPORTED_LANGUAGES[5]; // fallback en

  // Close on Escape key
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  const switchLang = (code: string) => {
    setOpen(false);
    const newPath = getPathWithLang(currentPath, code);
    i18n.changeLanguage(code).then(() => {
      localStorage.setItem('i18nextLng', code);
      window.location.href = newPath;
    });
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label="Select language"
        aria-expanded={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          background: open ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)',
          border: '1px solid rgba(255,255,255,0.3)',
          borderRadius: '6px',
          color: '#ffffff',
          fontSize: '13px',
          padding: '4px 8px',
          minHeight: '36px',
          cursor: 'pointer',
          fontFamily: 'inherit',
          transition: 'background 0.15s ease',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.22)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = open ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)'; }}
      >
        <span style={{ fontSize: '14px', lineHeight: 1 }}>{currentLangData.flag}</span>
        <span style={{ fontWeight: 600, letterSpacing: '0.03em' }}>{currentLang.toUpperCase()}</span>
        <span style={{ fontSize: '9px', opacity: 0.7, marginLeft: '1px' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 998, cursor: 'default' }} onClick={() => setOpen(false)} aria-hidden="true" />
          <div style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 999,
            background: '#1a56db', border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            minWidth: '200px', maxHeight: '320px', overflowY: 'auto', padding: '4px',
          }}>
            {SUPPORTED_LANGUAGES.map(lang => (
              <button
                key={lang.code}
                type="button"
                onClick={() => switchLang(lang.code)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  width: '100%', padding: '7px 10px',
                  background: lang.code === currentLang ? 'rgba(255,255,255,0.2)' : 'transparent',
                  border: 'none', borderRadius: '5px', color: '#ffffff',
                  fontSize: '13px', cursor: 'pointer', textAlign: 'left',
                  fontFamily: 'inherit', transition: 'background 0.1s ease',
                }}
                onMouseEnter={e => { if (lang.code !== currentLang) e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                onMouseLeave={e => { if (lang.code !== currentLang) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontSize: '16px', lineHeight: 1, flexShrink: 0 }}>{lang.flag}</span>
                <span style={{ flex: 1 }}>{lang.nativeName}</span>
                {lang.code === currentLang && <span style={{ fontSize: '11px', opacity: 0.8 }}>✓</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const ChampionshipBannerInner: React.FC<ChampionshipBannerProps> = ({ ratingSlot }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const playersToday = useMemo(() => dailyRandom(500, 2000), []);
  const [location] = useLocation();
  const isHome = location === '/';

  // Always visible on all pages (including Home).
  // Shadow appears once the user scrolls past 4px.
  const [scrolledPast] = useState(true);
  const [hasShadow, setHasShadow] = useState(false);

  useEffect(() => {
    const onScroll = () => setHasShadow(window.scrollY > 4);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Expose banner height as CSS custom property for scroll-margin-top
  useEffect(() => {
    document.documentElement.style.setProperty('--banner-height', `${BANNER_HEIGHT}px`);
  }, []);

  const handleGear = useCallback(() => {
    window.dispatchEvent(new CustomEvent('open-system-menu'));
  }, []);

  return (
    <>
    <style>{`
      @media (max-width: 600px) {
        .banner-badge { display: none !important; }
        .banner-players { display: none !important; }
        .championship-banner { padding-inline: 6px !important; gap: 4px !important; }
        .banner-home-label { display: none !important; }
      }
    `}</style>
    <div
      className="championship-banner"
      role="banner"
      aria-label={t('banner.aria_label')}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: 'linear-gradient(90deg, #1557b0 0%, #1a73e8 50%, #4285f4 100%)',
        color: '#ffffff',
        padding: '6px 12px',
        fontFamily: "'Segoe UI', Roboto, system-ui, sans-serif",
        fontSize: '13px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'nowrap',
        overflow: 'visible',
        // Slide in/out: translateY(-100%) when hidden, translateY(0) when visible
        transform: scrolledPast ? 'translateY(0)' : 'translateY(-100%)',
        transition: 'transform 0.3s cubic-bezier(0.23, 1, 0.32, 1), box-shadow 0.2s ease',
        boxShadow: hasShadow ? '0 2px 8px rgba(0,0,0,0.18)' : '0 2px 6px rgba(0,0,0,0.12)',
        // Keep in layout even when hidden so sticky works correctly
        // (translateY moves it off-screen without removing it from flow)
        pointerEvents: scrolledPast ? 'auto' : 'none',
      }}
    >
      {/* ── Left: SEO-optimised home link ── */}
      <a
        href="https://chromedinogame.com"
        title="Chrome Dino Game — Play the Chrome Dinosaur Game Online"
        aria-label="Chrome Dino Game — Chrome Dino Game home"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: '#ffffff',
          textDecoration: 'none',
          fontWeight: 600,
          letterSpacing: '0.01em',
        }}
      >
        <span style={{ fontSize: '16px' }} aria-hidden="true">🦕</span>
        <span className="banner-home-label">{t('banner.home_link')}</span>
        {/* sr-only keyword span */}
        <span
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0,0,0,0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          Chrome Dinosaur Game — Chrome Dino Game — Chrome Dino
        </span>
      </a>

      {/* Badge and player count removed per user request */}

      {/* ── Spacer ── */}
      <span style={{ flex: 1, minWidth: 0 }} />

      {/* ── Right: action buttons ── */}
      {ratingSlot}

      <a href={localizedPath('/leaderboard')} style={pillStyle}>
        {t('banner.leaderboard')}
      </a>

      {!isAuthenticated && (
        <a
          href={getLoginUrl('/')}
          style={pillStyle}
          aria-label={t('auth.sign_in_with_google')}
        >
          {t('banner.sign_in')}
        </a>
      )}

      {/* Language picker — custom dropdown blending into blue banner */}
      <LanguagePicker currentPath={location} />

      <button
        type="button"
        onClick={handleGear}
        aria-label={isAuthenticated ? 'Open settings (signed in)' : 'Open settings'}
        title="Settings"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '8px',
          minWidth: '44px',
          minHeight: '44px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(255,255,255,0.85)',
          borderRadius: '50%',
          transition: 'background 0.15s ease, color 0.15s ease',
          lineHeight: 1,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
          e.currentTarget.style.color = '#ffffff';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'none';
          e.currentTarget.style.color = 'rgba(255,255,255,0.85)';
        }}
      >
        <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 0 1-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 0 1 .947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 0 1 2.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 0 1 2.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 0 1 .947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 0 1-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 0 1-2.287-.947zM10 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </div>
    </>
  );
};

export const ChampionshipBanner = ChampionshipBannerInner;
ChampionshipBanner.displayName = 'ChampionshipBanner';
